import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, DollarSign, Clock, Briefcase, Building2, Users,
  Wifi, ExternalLink, Bookmark, BookmarkCheck, ArrowLeft,
  CheckCircle, Loader2, AlertCircle, X, Share2, Zap, Sparkles, FileText
} from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { translateError } from "../utils/i18nErrors";
import JobCard, { formatSalary } from "../components/JobCard";
import { formatJobLocation } from "../utils/formatLocation";
import { shareJob } from "../utils/shareJob";
import { companySizeLabel } from "../utils/companySize";
import JobMatchCard from "../components/JobMatchCard";
import AdaptCVModal from "../components/AdaptCVModal";
import { jobTypeLabel, experienceLabel, categoryLabel } from "../config/jobTaxonomy";
import { stripMarkdown } from "../utils/textFormat";
import { track } from "../lib/analytics";
import { VerificationBadge } from "../components/VerificationBadge";
import ReportJobModal from "../components/ReportJobModal";

function timeAgo(dateStr, t) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return t("jobCard.today");
  if (diff === 1) return t("jobCard.yesterday");
  if (diff < 7) return `${diff}${t("jobCard.dAgo")}`;
  if (diff < 30) return `${Math.floor(diff / 7)}${t("jobCard.wAgo")}`;
  return `${Math.floor(diff / 30)} ${t("jobCard.moAgo")}`;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPE_COLORS = {
  "full-time": "bg-white/15 text-white",
  "part-time": "bg-white/10 text-white",
  "contract": "bg-amber-400/20 text-amber-100",
  "internship": "bg-purple-400/20 text-purple-100",
};

/* ─── Apply Modal ─────────────────────────────────────────────────────────── */
function ApplyModal({ job, onClose }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", cover_letter: "", linkedin: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [cvLoading, setCvLoading] = useState(true);
  const [userCV, setUserCV] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedCv, setSelectedCv] = useState("primary"); // "primary" | `adapted:${version_id}`
  const [showAdaptModal, setShowAdaptModal] = useState(false);
  const [usedCvLabel, setUsedCvLabel] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API}/cv`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      axios.get(`${API}/cv-versions`, { headers: getAuthHeaders() }).catch(() => ({ data: { versions: [] } })),
    ]).then(([cvRes, versionsRes]) => {
      if (!active) return;
      setUserCV(cvRes.data && cvRes.data.user_id ? cvRes.data : null);
      setVersions(versionsRes.data?.versions || []);
      setCvLoading(false);
    });
    return () => { active = false; };
  }, []);

  const hasRecommendedVersion = versions.some((v) => v.target_job_id === job.job_id);

  const refreshVersions = () => {
    axios.get(`${API}/cv-versions`, { headers: getAuthHeaders() }).then((r) => setVersions(r.data?.versions || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const isAdapted = selectedCv.startsWith("adapted:");
    const chosenLabel = isAdapted
      ? (versions.find((v) => `adapted:${v.version_id}` === selectedCv)?.version_name || t("cvVersions.adaptedBadge"))
      : t("jobDetailExtra.primaryCvLabel");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("cv_type", isAdapted ? "adapted" : "primary");
      if (isAdapted) fd.append("cv_version_id", selectedCv.replace("adapted:", ""));
      await axios.post(`${API}/jobs/${job.job_id}/apply`, fd, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setUsedCvLabel(chosenLabel);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("Selected CV version not found")) {
        setError(t("jobDetailExtra.versionDeletedError"));
        refreshVersions();
        setSelectedCv("primary");
      } else {
        setError(translateError(detail, t) || t("errors.unknownError"));
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="apply-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-start justify-between rounded-t-2xl">
          <div>
            <h2 className="font-poppins text-lg font-bold text-slate-900">{t("jobDetailExtra.applyPosition")}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{job.title} · {job.company_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors mt-0.5" data-testid="close-modal">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {success ? (
          <div className="p-10 text-center" data-testid="apply-success">
            <div className="h-16 w-16 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="font-poppins text-xl font-bold text-slate-900 mb-2">{t("jobDetailExtra.successTitle")}</h3>
            <p className="text-slate-500 text-sm mb-1">{t("jobDetailExtra.successMsg")}</p>
            <p className="text-sm text-slate-700 font-semibold mt-3" data-testid="apply-success-job">{job.company_name} · {job.title}</p>
            <p className="text-xs text-slate-400 mb-6" data-testid="apply-success-cv-used">{t("jobDetailExtra.usedCvLabel")}: {usedCvLabel}</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/dashboard" onClick={onClose} data-testid="view-my-applications-link"
                className="h-11 px-5 flex items-center border border-[#028CF8]/30 text-[#028CF8] font-semibold rounded-xl hover:bg-[#028CF8]/5 transition-all text-sm">
                {t("jobDetailExtra.viewApplicationsCta")}
              </Link>
              <button onClick={onClose} className="btn-primary rounded-xl">{t("jobDetailExtra.continue")}</button>
            </div>
          </div>
        ) : cvLoading ? (
          <div className="p-16 flex items-center justify-center" data-testid="apply-cv-loading">
            <Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" />
          </div>
        ) : !userCV ? (
          <div className="p-10 text-center" data-testid="apply-no-cv">
            <div className="h-14 w-14 rounded-2xl bg-[#028CF8]/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-7 w-7 text-[#028CF8]" />
            </div>
            <h3 className="font-poppins text-lg font-bold text-slate-900 mb-2">{t("jobDetailExtra.noCvTitle")}</h3>
            <p className="text-slate-500 text-sm mb-6">{t("jobDetailExtra.noCvDesc")}</p>
            <Link to="/cv-builder" data-testid="apply-create-cv-link" className="btn-primary rounded-xl inline-flex">
              {t("jobDetailExtra.createCvCta")}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="apply-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.fullNameReq")}</label>
                <input data-testid="apply-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.emailReq")}</label>
                <input data-testid="apply-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.phoneReq")}</label>
              <input data-testid="apply-phone" required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+1 (555) 000-0000"
                className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.linkedinOpt")}</label>
              <input data-testid="apply-linkedin" value={form.linkedin} onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))} placeholder="https://linkedin.com/in/..."
                className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
            </div>

            {/* CV selector — CV principal or one of the candidate's saved adapted versions */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.selectCvTitle")}</label>
              <div className="space-y-2" data-testid="apply-cv-selector">
                <label data-testid="apply-cv-option-primary"
                  className={`flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 cursor-pointer transition-all ${selectedCv === "primary" ? "border-[#028CF8] bg-[#028CF8]/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="cv-choice" checked={selectedCv === "primary"} onChange={() => setSelectedCv("primary")} className="accent-[#028CF8]" />
                  <span className="text-sm font-semibold text-slate-800">{t("jobDetailExtra.primaryCvLabel")}</span>
                </label>
                {versions.map((v) => {
                  const value = `adapted:${v.version_id}`;
                  const recommended = v.target_job_id === job.job_id;
                  return (
                    <label key={v.version_id} data-testid={`apply-cv-option-${v.version_id}`}
                      className={`flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 cursor-pointer transition-all ${selectedCv === value ? "border-[#028CF8] bg-[#028CF8]/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="radio" name="cv-choice" checked={selectedCv === value} onChange={() => setSelectedCv(value)} className="accent-[#028CF8]" />
                      <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{v.version_name}</span>
                      {recommended && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#F55FA5]/10 text-[#F55FA5] flex-shrink-0">
                          {t("jobDetailExtra.recommendedBadge")}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {!hasRecommendedVersion && (
                <button type="button" onClick={() => setShowAdaptModal(true)} data-testid="apply-adapt-cv-cta"
                  className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#F55FA5] hover:underline">
                  <Sparkles className="h-3.5 w-3.5" /> {t("jobDetailExtra.adaptCvSecondaryCta")}
                </button>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("jobDetailExtra.coverLetter")}</label>
              <textarea data-testid="apply-cover-letter" rows={4} value={form.cover_letter} onChange={(e) => setForm((f) => ({ ...f, cover_letter: e.target.value }))}
                placeholder={t("jobDetailExtra.coverHint")}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
            </div>
            {error && (
              <div data-testid="apply-error" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="submit-application"
              className="w-full h-12 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("jobDetailExtra.submitApp")}
            </button>
          </form>
        )}
      </div>
      {showAdaptModal && (
        <AdaptCVModal
          jobId={job.job_id} jobTitle={job.title} companyName={job.company_name}
          onClose={() => setShowAdaptModal(false)}
          onSaved={(version) => {
            setVersions((prev) => [version, ...prev]);
            setSelectedCv(`adapted:${version.version_id}`);
          }}
        />
      )}
    </div>
  );
}

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  const heroCtaRef = useRef(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!job || !heroCtaRef.current) return;
    const observer = new IntersectionObserver(([entry]) => setHeroCtaVisible(entry.isIntersecting), { threshold: 0 });
    observer.observe(heroCtaRef.current);
    return () => observer.disconnect();
  }, [job]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`, { headers: getAuthHeaders() });
        setJob(res.data);
        setSaved(res.data.is_saved || false);
        track("job_viewed", { job_id: id, category: res.data.category, source_type: res.data.source_type || "direct" });
      } catch { navigate("/jobs"); }
      finally { setLoading(false); }
    };
    load();
  }, [id, navigate]);

  const toggleSave = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      if (saved) {
        await axios.delete(`${API}/saved-jobs/${id}`, { headers: getAuthHeaders() });
        setSaved(false);
        track("job_saved", { job_id: id, saved: false });
      } else {
        await axios.post(`${API}/saved-jobs/${id}`, {}, { headers: getAuthHeaders() });
        setSaved(true);
        track("job_saved", { job_id: id, saved: true });
      }
    } catch {}
  };

  const handleShare = () => shareJob(job, formatJobLocation(job), t("share.intro"), () => toast.success(t("share.linkCopied")));

  const handleApply = () => {
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "candidate") return;
    track("application_started", { job_id: id });
    if (job.external_apply_link) {
      window.open(job.external_apply_link, "_blank");
    } else {
      setShowApply(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div data-testid="job-detail-page" className="min-h-screen bg-white">
      {showApply && <ApplyModal job={job} onClose={() => setShowApply(false)} />}
      {showReport && <ReportJobModal jobId={id} onClose={() => setShowReport(false)} />}

      {/* ── Large Header — same dark-gradient as InfoCityProp detail page ── */}
      <section className="hero-infocity py-4 sm:py-10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:underline text-sm mb-2.5 sm:mb-5 transition-colors" data-testid="back-button">
            <ArrowLeft className="h-4 w-4" /> {t("jobDetail.back")}
          </button>

          {/* ── MOBILE — versión compacta (mismos datos y acciones, menos altura) ── */}
          <div className="sm:hidden" data-testid="job-detail-hero-mobile">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="h-7 w-7 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center font-poppins font-bold text-white text-xs flex-shrink-0 overflow-hidden">
                {job.company_logo ? (
                  <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-cover" />
                ) : (
                  job.company_name?.[0]?.toUpperCase()
                )}
              </div>
              <p className="text-white font-semibold text-sm truncate flex items-center gap-1.5">{job.company_name}{job.company_verified && <VerificationBadge className="!bg-white/15 !border-white/30 !text-white" />}</p>
            </div>
            <h1 className="font-poppins text-xl font-bold text-white leading-tight break-words mb-1.5" data-testid="job-title-mobile">
              {job.title}
            </h1>
            <p className="flex items-center gap-1.5 text-sm text-white mb-3 truncate">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{formatJobLocation(job)}</span>
            </p>

            {/* CTA compacta — sentinel del sticky bar mobile */}
            <div ref={heroCtaRef} className="flex items-center gap-2 mb-3">
              <button
                onClick={handleShare}
                data-testid="share-job-detail-mobile"
                aria-label={t("jobDetail.shareAria")}
                title={t("jobDetail.shareAria")}
                className="h-9 w-9 rounded-xl border border-white/30 text-white hover:border-white/50 flex items-center justify-center transition-all flex-shrink-0"
              >
                <Share2 className="h-4 w-4" />
              </button>
              <button
                onClick={toggleSave}
                data-testid="save-job-detail-mobile"
                className={`h-9 w-9 rounded-xl border flex items-center justify-center transition-all flex-shrink-0 ${saved ? "border-[#028CF8] bg-[#028CF8]/20 text-white" : "border-white/30 text-white hover:border-white/50"}`}
              >
                {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              </button>
              {job.has_applied ? (
                <div className="flex-1 flex items-center justify-center gap-1.5 bg-green-500/20 border border-green-400/30 text-green-200 text-sm font-semibold h-9 rounded-xl" data-testid="already-applied-mobile">
                  <CheckCircle className="h-3.5 w-3.5" /> {t("jobDetail.alreadyApplied")}
                </div>
              ) : job.status !== "active" ? (
                <div className="flex-1 flex items-center justify-center gap-1.5 bg-white/10 border border-white/20 text-white text-sm font-semibold h-9 rounded-xl" data-testid="job-closed-badge-mobile">
                  {t("jobDetail.jobClosedTitle")}
                </div>
              ) : (
                <button
                  onClick={handleApply}
                  data-testid="apply-button-mobile"
                  disabled={user?.role === "employer"}
                  className="flex-1 h-9 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl text-sm transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {job.external_apply_link ? <><ExternalLink className="h-3.5 w-3.5" /> {t("jobDetail.applyExternally")}</> : t("jobDetail.applyNow")}
                </button>
              )}
            </div>

            {/* Chips — igual info que desktop, más compactas */}
            <div className="flex flex-wrap gap-2">
              {job.salary_visible !== false && (job.salary_min || job.salary_max) && (
                <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/15 text-white">
                  {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                  <span className="font-normal opacity-80 ml-0.5">{t(`jobCard.${{ hour: "perHour", month: "perMonth" }[job.salary_period] || "perYear"}`)}</span>
                </span>
              )}
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${JOB_TYPE_COLORS[job.job_type?.replace(/_/g, "-")] || "bg-white/10 text-white"}`}>
                {jobTypeLabel(job.job_type, t)}
              </span>
              <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 text-white capitalize">
                {experienceLabel(job.experience_level, t)}
              </span>
              {job.work_mode === "hybrid" && (
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-400/20 text-teal-100">
                  {t("jobCard.hybrid")}
                </span>
              )}
              {(job.work_mode ? job.work_mode === "remote" : job.remote) && (
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-400/20 text-teal-100 flex items-center gap-1">
                  <Wifi className="h-3 w-3" /> {t("jobCard.remote")}
                </span>
              )}
              {(job.is_urgent || job.start_date === todayStr) && (
                <span
                  data-testid="urgency-badge-detail-mobile"
                  className="text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1"
                  style={{
                    backgroundColor: job.is_urgent ? "rgba(245,95,165,0.22)" : "rgba(255,255,255,0.18)",
                    color: job.is_urgent ? "#F55FA5" : "white",
                  }}
                >
                  <Zap className="h-3 w-3" />
                  {job.is_urgent ? t("jobDetail.urgentBadge") : job.urgency_label === "INMEDIATA" ? t("jobDetail.immediateBadge") : job.urgency_label || t("jobDetail.todayBadge")}
                </span>
              )}
            </div>
          </div>

          {/* ── TABLET/DESKTOP — layout original, sin cambios ── */}
          <div className="hidden sm:block">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
              {/* Company avatar */}
              <div className="h-10 w-10 sm:h-16 sm:w-16 rounded-xl sm:rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center font-poppins font-bold text-white text-base sm:text-2xl flex-shrink-0 overflow-hidden" data-testid="job-detail-company-avatar">
                {job.company_logo ? (
                  <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-cover" />
                ) : (
                  job.company_name?.[0]?.toUpperCase()
                )}
              </div>

              <div className="flex-1 min-w-0 w-full">
                <p className="text-white font-semibold text-sm mb-1 break-words flex items-center gap-1.5">{job.company_name}{job.company_verified && <VerificationBadge className="!bg-white/15 !border-white/30 !text-white" />}</p>
                <h1 className="font-poppins text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight break-words" data-testid="job-title">
                  {job.title}
                </h1>
                <div className="flex flex-wrap gap-3 mt-2 sm:mt-3 text-sm text-white">
                  {formatJobLocation(job) && (
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {formatJobLocation(job)}</span>
                  )}
                  {job.salary_visible !== false && (job.salary_min || job.salary_max) && (
                    <span className="flex items-center gap-1.5 text-white font-semibold">
                      {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                      <span className="font-normal opacity-80">{t(`jobCard.${{ hour: "perHour", month: "perMonth" }[job.salary_period] || "perYear"}`)}</span>
                    </span>
                  )}
                  <span className="hidden sm:flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{timeAgo(job.posted_date, t)}</span>
                </div>
              </div>

              {/* CTA in header */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleShare}
                  data-testid="share-job-detail"
                  aria-label={t("jobDetail.shareAria")}
                  title={t("jobDetail.shareAria")}
                  className="h-10 w-10 rounded-xl border border-white/30 text-white hover:border-white/50 flex items-center justify-center transition-all"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button
                  onClick={toggleSave}
                  data-testid="save-job-detail"
                  className={`h-10 w-10 rounded-xl border flex items-center justify-center transition-all ${saved ? "border-[#028CF8] bg-[#028CF8]/20 text-white" : "border-white/30 text-white hover:border-white/50"}`}
                >
                  {saved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
                </button>
                {job.has_applied ? (
                  <div className="flex items-center gap-2 bg-green-500/20 border border-green-400/30 text-green-200 text-base font-semibold px-4 py-2 rounded-xl" data-testid="already-applied">
                    <CheckCircle className="h-4 w-4" /> {t("jobDetail.alreadyApplied")}
                  </div>
                ) : job.status !== "active" ? (
                  <div className="flex items-center gap-2 bg-white/10 border border-white/20 text-white text-base font-semibold px-4 py-2 rounded-xl" data-testid="job-closed-badge">
                    {t("jobDetail.jobClosedTitle")}
                  </div>
                ) : (
                  <button
                    onClick={handleApply}
                    data-testid="apply-button"
                    disabled={user?.role === "employer"}
                    className="h-10 px-6 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl text-base transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
                  >
                    {job.external_apply_link ? <><ExternalLink className="h-4 w-4" /> {t("jobDetail.applyExternally")}</> : t("jobDetail.applyNow")}
                  </button>
                )}
              </div>
            </div>

            {/* Badges row */}
            <div className="flex flex-wrap gap-2 mt-3 sm:mt-5">
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${JOB_TYPE_COLORS[job.job_type?.replace(/_/g, "-")] || "bg-white/10 text-white"}`}>
                {jobTypeLabel(job.job_type, t)}
              </span>
              <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 text-white capitalize">
                {experienceLabel(job.experience_level, t)}
              </span>
              {job.work_mode === "hybrid" && (
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-400/20 text-teal-100">
                  {t("jobCard.hybrid")}
                </span>
              )}
              {(job.work_mode ? job.work_mode === "remote" : job.remote) && (
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-400/20 text-teal-100 flex items-center gap-1">
                  <Wifi className="h-3 w-3" /> {t("jobCard.remote")}
                </span>
              )}
              <span className="hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 text-white">
                {categoryLabel(job.category, t)}
              </span>
              {(job.is_urgent || job.start_date === todayStr) && (
                <span
                  data-testid="urgency-badge-detail"
                  className="text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1"
                  style={{
                    backgroundColor: job.is_urgent ? "rgba(245,95,165,0.22)" : "rgba(255,255,255,0.18)",
                    color: job.is_urgent ? "#F55FA5" : "white",
                  }}
                >
                  <Zap className="h-3 w-3" />
                  {job.is_urgent ? t("jobDetail.urgentBadge") : job.urgency_label === "INMEDIATA" ? t("jobDetail.immediateBadge") : job.urgency_label || t("jobDetail.todayBadge")}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA — only visible once the original header CTA has scrolled out of view.
          Sits ABOVE the persistent guest/candidate Bottom Nav so it's never covered/unclickable
          (employers have no Bottom Nav, so their CTA still sits flush at the very bottom). */}
      {!heroCtaVisible && job.status === "active" && !job.has_applied && (
        <div
          className="sm:hidden fixed left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] px-4 py-3 flex items-center gap-2"
          style={{
            bottom: user?.role === "employer" ? 0 : "calc(64px + env(safe-area-inset-bottom))",
            paddingBottom: user?.role === "employer" ? "calc(12px + env(safe-area-inset-bottom))" : "12px",
          }}
          data-testid="sticky-mobile-cta"
        >
          <button
            onClick={toggleSave}
            data-testid="sticky-save-job"
            className={`h-11 w-11 flex-shrink-0 rounded-xl border flex items-center justify-center transition-all ${saved ? "border-[#028CF8] bg-[#028CF8]/10 text-[#028CF8]" : "border-slate-200 text-slate-500"}`}
          >
            {saved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
          </button>
          <button
            onClick={handleApply}
            data-testid="sticky-apply-button"
            disabled={user?.role === "employer"}
            className="flex-1 h-11 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl text-base transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {job.external_apply_link ? <><ExternalLink className="h-4 w-4" /> {t("jobDetail.applyExternally")}</> : t("jobDetail.applyNow")}
          </button>
        </div>
      )}

      {/* ── Content — Two-column like InfoCityProp property detail ── */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-10 pb-24 sm:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main column — 2/3 */}
          <div className="lg:col-span-2 space-y-6">


            {/* Description */}
            <div className="bg-white border border-slate-100 rounded-2xl p-7" data-testid="job-description">
              <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-3">{t("jobDetail.aboutRole")}</p>
              <h2 className="font-poppins text-xl font-bold text-slate-900 mb-4">{t("jobDetail.description")}</h2>
              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap break-words">{stripMarkdown(job.description)}</p>
            </div>

            {/* Requirements */}
            {job.requirements?.length > 0 && (
              <div className="bg-white border border-slate-100 rounded-2xl p-7" data-testid="job-requirements">
                <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-3">{t("jobDetail.whatWeNeed")}</p>
                <h2 className="font-poppins text-xl font-bold text-slate-900 mb-5">{t("jobDetail.requirements")}</h2>
                <ul className="space-y-3">
                  {job.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600 min-w-0">
                      <CheckCircle className="h-4 w-4 text-[#028CF8] mt-0.5 flex-shrink-0" />
                      <span className="break-words">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {job.benefits?.length > 0 && (
              <div className="bg-white border border-slate-100 rounded-2xl p-7" data-testid="job-benefits">
                <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-3">{t("jobDetail.perksLabel")}</p>
                <h2 className="font-poppins text-xl font-bold text-slate-900 mb-5">{t("jobDetail.benefits")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {job.benefits.map((b, i) => (
                    <div key={i} className="flex items-center gap-2.5 bg-green-50 border border-green-100 rounded-xl px-4 py-2.5 text-sm text-slate-700 min-w-0">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> <span className="break-words">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — 1/3 */}
          <div className="space-y-5">

            {/* Apply card — prominent, always visible */}
            {!job.has_applied && job.status === "active" && (
              <div className="bg-gradient-to-br from-[#028CF8] to-[#0270C5] rounded-2xl p-6 text-white" data-testid="apply-sidebar-card">
                <h3 className="font-poppins font-bold text-lg mb-1">{t("jobDetail.interested")}</h3>
                <p className="text-white text-sm mb-5">{t("jobDetail.applyDesc")}</p>
                {user?.role === "candidate" ? (
                  <button onClick={handleApply} data-testid="apply-button-sidebar"
                    className="w-full h-12 bg-white text-[#028CF8] font-bold rounded-xl hover:bg-slate-50 transition-all text-base flex items-center justify-center gap-2 shadow-lg">
                    {job.external_apply_link ? <><ExternalLink className="h-4 w-4" /> {t("jobDetail.applyExternally")}</> : t("jobDetail.applyNow")}
                  </button>
                ) : !user ? (
                  <Link to="/auth" className="w-full h-12 bg-white text-[#028CF8] font-bold rounded-xl hover:bg-slate-50 transition-all text-base flex items-center justify-center gap-2 shadow-lg">
                    {t("jobDetail.signInToApply")}
                  </Link>
                ) : null}
              </div>
            )}
            {!job.has_applied && job.status !== "active" && (
              <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6 text-center" data-testid="apply-sidebar-closed">
                <p className="text-sm font-semibold text-slate-500">{t("jobDetail.jobClosedTitle")}</p>
                <p className="text-xs text-slate-500 mt-1">{t("jobDetail.jobClosedDesc")}</p>
              </div>
            )}

            {/* CV <-> Job compatibility — real, deterministic analysis (candidates only) */}
            <JobMatchCard jobId={job.job_id} jobTitle={job.title} companyName={job.company_name} />

            {/* Job Summary */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="job-summary">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4">{t("jobDetail.jobDetails")}</p>
              <div className="space-y-3 text-sm">
                {[
                  { icon: Briefcase, label: t("jobDetail.jobTypeLabel"),   value: jobTypeLabel(job.job_type, t) },
                  { icon: Building2, label: t("jobDetail.categoryLabel"),  value: categoryLabel(job.category, t) },
                  { icon: Users,     label: t("jobDetail.experienceLabel"),value: experienceLabel(job.experience_level, t) },
                  { icon: MapPin,    label: t("jobDetail.locationLabel"),  value: formatJobLocation(job) || (job.work_mode === "remote" ? t("jobCard.remote") : "") },
                  { icon: Clock,     label: t("jobDetail.postedLabel"),    value: timeAgo(job.posted_date, t) },
                  { icon: Users,     label: t("jobDetail.applicantsLabel"),value: `${job.applications_count || 0} ${t("jobDetail.people")}` },
                  ...(job.shift_label ? [{ icon: Clock, label: t("jobDetail.shiftLabel"), value: job.shift_label }] : []),
                  ...(job.work_mode === "remote" && job.remote_countries?.length
                    ? [{ icon: Wifi, label: t("jobDetail.remoteFromLabel"), value: job.remote_countries.map((c) => t(`geo.countryNames.${c}`)).join(", ") }]
                    : []),
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-3.5 w-3.5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400">{label}</p>
                      <p className="font-semibold text-slate-900 capitalize truncate" data-testid={label === t("jobDetail.shiftLabel") ? "shift-label-detail" : undefined}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Company card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="company-sidebar">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4">{t("jobDetail.companyLabel")}</p>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-[#028CF8]/10 flex items-center justify-center font-poppins font-bold text-[#028CF8] text-lg border border-slate-100 overflow-hidden" data-testid="job-detail-sidebar-company-avatar">
                  {job.company_logo ? (
                    <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-cover" />
                  ) : (
                    job.company_name?.[0]
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate flex items-center gap-1.5">
                    {job.company_name}
                    {job.company_verified && <VerificationBadge />}
                  </p>
                  {job.company?.industry && <p className="text-xs text-slate-500 truncate">{job.company.industry}</p>}
                </div>
              </div>
              {job.company?.description && (
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-3 mb-4">{job.company.description}</p>
              )}
              {job.company?.size && (
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <Users className="h-3.5 w-3.5 text-slate-400" /> {companySizeLabel(job.company.size, t)}
                </div>
              )}
              {job.company?.company_id && (
                <Link
                  to={`/companies/${job.company.company_id}`}
                  className="mt-4 block w-full text-center h-9 border border-[#028CF8]/30 text-[#028CF8] text-xs font-semibold rounded-xl hover:bg-[#028CF8]/5 transition-all flex items-center justify-center"
                >
                  {t("jobDetail.viewCompanyProfile")}
                </Link>
              )}
              {user && (
                <button
                  onClick={() => setShowReport(true)}
                  data-testid="report-job-button"
                  className="mt-3 block w-full text-center text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  {t("jobDetail.reportJob")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
